import React, {createContext, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {NOTIFICATION_VARIANTS} from '@nur-portal/ui-kit';

import {handleLoginEvent} from '../../modules/auth-buttons';
import {AVAILABLE_REACTION_LIST} from '../../modules/comments/reactions/constants';
import {AUTH_MODAL_INITIATORS, dispatchArticleAuthModal} from '../components/article-auth-modal/constants';
import {showNotification} from '../components/notification-center/helpers';
import {useUser} from '../hooks/use-user';
import {reactionsHttpRepository} from '../repositories/reactions-http-repository';
import {reactionsMemoryRepository} from '../repositories/reactions-memory-repository';

const ReactionsContext = createContext({});

const sortReactionCount = (currentReaction, nextReaction) => nextReaction.count - currentReaction.count;
const sortReactionOrder = (currentReaction, nextReaction) => {
  if (currentReaction.count === nextReaction.count) {
    return AVAILABLE_REACTION_LIST.indexOf(currentReaction.type) - AVAILABLE_REACTION_LIST.indexOf(nextReaction.type);
  }
};

const sortReactions = reactions => reactions
  .sort(sortReactionCount)
  .sort(sortReactionOrder);

const getSortedReactions = articleReactions => {
  if (Object.values(articleReactions).length > 0) {
    return Object.keys(articleReactions).reduce((reactions, entityId) => {
      reactions[entityId] = sortReactions(articleReactions[entityId]);
      return reactions;
    }, {});
  }
  return {};
};

const sendReaction = ({
  articleId,
  commentId,
  reaction,
  t
}) => {
  if (reaction.isUserReaction) {
    reactionsHttpRepository.deleteUserReaction(commentId)
      .then(() => {
        reactionsMemoryRepository.deleteCommentReaction(articleId, commentId);
      })
      .catch(() => {
        showNotification({
          variant: NOTIFICATION_VARIANTS.ALERT,
          text: t('reactions.publishError')
        });
      });
  } else {
    reactionsHttpRepository.setUserReaction(commentId, reaction.type)
      .then(response => {
        if (response.ok) {
          reactionsMemoryRepository.setCommentReaction(articleId, commentId, reaction.type);
          return;
        }
        if (response.status === 429) {
          showNotification({
            variant: NOTIFICATION_VARIANTS.ALERT,
            text: t('reactions.limitError')
          });
          return;
        }
        showNotification({
          variant: NOTIFICATION_VARIANTS.ALERT,
          text: t('reactions.publishError')
        });
      })
      .catch(() => {
        showNotification({
          variant: NOTIFICATION_VARIANTS.ALERT,
          text: t('reactions.publishError')
        });
      });
  }
};

const ReactionsProvider = ({
  articleId,
  children
}) => {
  const {t} = useTranslation();
  const [
    reactionsFromMemory,
    setReactionsFromMemory
  ] = useState(reactionsMemoryRepository.getReactions());

  reactionsMemoryRepository.subscribe((_, value) => setReactionsFromMemory(value));

  const user = useUser();

  const providedContext = {
    reactions: getSortedReactions(reactionsFromMemory?.[articleId] || {}),
    handleReactionClick: ({commentId, reaction}) => {
      if (!user.isAuthenticated) {
        dispatchArticleAuthModal(AUTH_MODAL_INITIATORS.COMMENT_REACTION);
        handleLoginEvent(() => {
          sendReaction({
            reaction,
            commentId,
            articleId,
            t
          });
        });
        return;
      }
      sendReaction({
        articleId,
        commentId,
        reaction,
        t
      });
    }
  };

  return (
    <ReactionsContext.Provider value={providedContext}>
      {children}
    </ReactionsContext.Provider>
  );
};

export {ReactionsProvider, ReactionsContext};
